const UNSURE_CONFIDENCE_THRESHOLD = 0.6;

export const PRESCRIPTION_EXTRACT_MESSAGES = {
  UNSURE:
    "The prescription could not be read with enough certainty. Open the file and compare the extracted values before continuing.",
  MULTIPLE_PRESCRIPTIONS:
    "This form appears to contain more than one prescription. Upload a single-patient prescription and try again.",
};

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function hasNumericAdd(eye = {}) {
  const add = toNumberOrNull(eye.add ?? eye.ADD);
  return add != null && add !== 0;
}

export function prescriptionHasAdd(prescription = {}, hasAddFlag) {
  if (hasAddFlag === true) return true;
  return (
    hasNumericAdd(prescription.od || {}) ||
    hasNumericAdd(prescription.os || {})
  );
}

export function evaluatePrescriptionExtraction(extraction = {}) {
  const issues = [];
  const confidence = Number(extraction.confidence);
  const unsure =
    extraction.unsure === true ||
    (Number.isFinite(confidence) && confidence < UNSURE_CONFIDENCE_THRESHOLD);
  const multiplePrescriptions = extraction.multiplePrescriptions === true;
  const prescription = extraction.prescription || extraction;
  const hasAdd = prescriptionHasAdd(prescription, extraction.hasAdd);

  if (multiplePrescriptions) {
    issues.push({
      code: "MULTIPLE_PRESCRIPTIONS",
      message: PRESCRIPTION_EXTRACT_MESSAGES.MULTIPLE_PRESCRIPTIONS,
      blocking: true,
    });
  }

  if (unsure) {
    issues.push({
      code: "UNSURE",
      message: PRESCRIPTION_EXTRACT_MESSAGES.UNSURE,
      blocking: false,
    });
  }

  return {
    unsure,
    multiplePrescriptions,
    hasAdd,
    issues,
    hasBlockingIssue: issues.some((issue) => issue.blocking),
  };
}

export function issuesForLensType(prescription = {}, _lensType = "", extractMeta = {}) {
  return evaluatePrescriptionExtraction({
    prescription,
    confidence: extractMeta.confidence,
    unsure: extractMeta.unsure,
    multiplePrescriptions:
      extractMeta.multiplePrescriptions === true ||
      prescription.multiplePrescriptions === true,
  }).issues;
}

export function hasBlockingPrescriptionIssue(issues = []) {
  return issues.some((issue) => issue.blocking);
}

function unwrapExtractPayload(response) {
  let payload = response?.data ?? response?.item ?? response;
  if (payload?.data && payload?.prescription != null && payload.parseId === undefined) {
    payload = payload.data;
  }
  return payload || {};
}

export function prescriptionFromExtract(extractInput, fileMeta = {}) {
  const extract = unwrapExtractPayload(extractInput);
  const rx = extract.prescription || {};
  const od = rx.od || {};
  const os = rx.os || {};
  const pdDistance = rx.pdDistance ?? rx.singularPd ?? null;
  const pdType = rx.pdType || (pdDistance != null ? "One" : null);

  return {
    ...rx,
    od: {
      sph: od.sph ?? od.sphere ?? null,
      sphere: od.sphere ?? od.sph ?? null,
      cyl: od.cyl ?? od.cylinder ?? null,
      cylinder: od.cylinder ?? od.cyl ?? null,
      axis: od.axis ?? null,
      add: od.add ?? null,
      pd: od.pd ?? null,
    },
    os: {
      sph: os.sph ?? os.sphere ?? null,
      sphere: os.sphere ?? os.sph ?? null,
      cyl: os.cyl ?? os.cylinder ?? null,
      cylinder: os.cylinder ?? os.cyl ?? null,
      axis: os.axis ?? null,
      add: os.add ?? null,
      pd: os.pd ?? null,
    },
    pdType,
    singularPd: pdDistance,
    pdDistance,
    fileName: fileMeta.fileName || null,
    fileUrl: fileMeta.fileUrl || null,
    prescriptionFileKey: extract.prescriptionFileKey || null,
    multiplePrescriptions: extract.multiplePrescriptions === true,
    hasAdd: extract.hasAdd === true,
    extractMeta: {
      confidence: extract.confidence ?? null,
      extractionNotes: extract.extractionNotes || "",
      unsure: extract.unsure === true,
      multiplePrescriptions: extract.multiplePrescriptions === true,
      inferredLensType: extract.inferredLensType || "",
      hasAdd: extract.hasAdd === true,
      issues: Array.isArray(extract.issues) ? extract.issues : [],
      snapshot: rx,
    },
  };
}

export function formatRxNumber(value, { axis = false } = {}) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (axis) return String(Math.round(n));
  return n.toFixed(2);
}

export function isPdfFile({ fileName, fileType, fileUrl } = {}) {
  const type = String(fileType || "").toLowerCase();
  if (type.includes("pdf")) return true;
  const name = String(fileName || fileUrl || "").toLowerCase();
  return name.includes(".pdf");
}

const ORDER_RX_FIELDS = [
  "ODRightSphere_SPH",
  "ODRightCylinder_CYL",
  "ODRightAxis",
  "ODRightADD",
  "ODRightPupillaryDistance_PD",
  "OSLeftSphere_SPH",
  "OSLeftCylinder_CYL",
  "OSLeftAxis",
  "OSLeftADD",
  "OSLeftPupillaryDistance_PD",
  "SingularPDValue",
];

export function getOrderPrescriptionFieldKeys() {
  return [...ORDER_RX_FIELDS, "PDOneorTwoNumbers"];
}

/** True when any prescription numeric field already has a saved/entered value. */
export function hasExistingPrescriptionValues(formValues = {}) {
  return ORDER_RX_FIELDS.some((field) => {
    const value = formValues?.[field];
    if (value === null || value === undefined || value === "") return false;
    return true;
  });
}

function guessFileNameFromUrl(url = "") {
  try {
    const pathname = new URL(url).pathname;
    const base = pathname.split("/").filter(Boolean).pop();
    if (base) return decodeURIComponent(base.split("?")[0]);
  } catch {
    /* ignore */
  }
  return "prescription.pdf";
}

/** Download a public prescription URL into a File for parse-prescription. */
export async function fetchPrescriptionFileAsFile(fileUrl) {
  const url = String(fileUrl || "").trim();
  if (!url) {
    throw new Error("No prescription file found for this order.");
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Could not download the prescription file. Try opening it first.");
  }

  const blob = await response.blob();
  const contentType =
    blob.type ||
    response.headers.get("content-type") ||
    (isPdfFile({ fileUrl: url }) ? "application/pdf" : "application/octet-stream");
  const fileName = guessFileNameFromUrl(url);

  return new File([blob], fileName, { type: contentType });
}
